import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  CalendarRange, Plus, Search, LayoutGrid, List, Trash2, Pencil, X, CheckCircle2,
  AlertTriangle, TrendingUp, TrendingDown, Wallet, Clock, MapPin, User, RefreshCw,
  BarChart3, FileDown, FileSpreadsheet
} from 'lucide-react';
import { OrgFinance, OrgFinancialReport } from './OrgFinance';
import { OrgReports, downloadReport } from './OrgReports';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import ProTable from '../ui-pro/ProTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`;
const trDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('tr-TR') : '-');
const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const STATUS = {
  planlandi: { label: 'Planlandı', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  aktif: { label: 'Aktif', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  tamamlandi: { label: 'Tamamlandı', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
  iptal: { label: 'İptal Edildi', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

const emptyOrg = {
  name: '', org_type: 'Kurumsal Turnuva', customer_id: '', client_company: '', contact_name: '',
  contact_phone: '', contact_email: '', responsible_personnel_id: '', description: '',
  internal_notes: '', status: 'planlandi', planned_income: '', planned_expense: '',
};

const OrganizationsTab = ({ salons = [], customers = [], personnelList = [], isAdmin = false, onReservationsChanged, autoOpenNew = false, onAutoOpenConsumed }) => {
  const [summary, setSummary] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');
  const [mainView, setMainView] = useState('list');
  const [filters, setFilters] = useState({ status: 'all', org_type: 'all', salon_id: 'all', responsible: 'all', profitability: 'all', search: '', start: '', end: '' });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyOrg);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [planMode, setPlanMode] = useState('manual'); // manual | recurring
  const [sessionDraft, setSessionDraft] = useState({ date: '', start_time: '15:00', end_time: '22:00', salon_id: '', title: '' });
  const [sessions, setSessions] = useState([]);
  const [recurrence, setRecurrence] = useState({ mode: 'weekly', start_date: '', end_date: '', weekdays: [], start_time: '15:00', end_time: '22:00', salon_id: '' });

  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [showSessionAdd, setShowSessionAdd] = useState(false);
  const [editingSession, setEditingSession] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (filters.start) params.start = filters.start;
      if (filters.end) params.end = filters.end;
      const res = await axios.get(`${API}/organizations/summary`, { params });
      setSummary(res.data);
    } catch (e) { /* sessiz */ }
  }, [filters.start, filters.end]);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== 'all') params[k] = v; });
      const res = await axios.get(`${API}/organizations`, { params });
      setOrgs(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Organizasyonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadOrgs(); loadSummary(); }, [loadOrgs, loadSummary]);
  useEffect(() => {
    axios.get(`${API}/organization-types`).then(r => setTypes(r.data?.items || [])).catch(() => {});
  }, []);

  // Üst bardaki "Yeni Organizasyon" hızlı işlemi
  useEffect(() => {
    if (!autoOpenNew) return;
    setMainView('list');
    resetForm();
    setShowForm(true);
    onAutoOpenConsumed?.();
  }, [autoOpenNew]); // eslint-disable-line

  const resetForm = () => {
    setForm(emptyOrg); setSessions([]); setEditingId(null); setPlanMode('manual');
    setSessionDraft({ date: '', start_time: '15:00', end_time: '22:00', salon_id: '', title: '' });
    setRecurrence({ mode: 'weekly', start_date: '', end_date: '', weekdays: [], start_time: '15:00', end_time: '22:00', salon_id: '' });
  };

  const addSessionToDraft = () => {
    if (!sessionDraft.date || !sessionDraft.salon_id) { toast.error('Tarih ve salon seçin'); return; }
    setSessions([...sessions, { ...sessionDraft }]);
    setSessionDraft({ ...sessionDraft, date: '', title: '' });
  };

  const submitOrg = async () => {
    if (!form.name.trim()) { toast.error('Organizasyon adı zorunlu'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/organizations/${editingId}`, {
          ...form,
          planned_income: Number(form.planned_income) || 0,
          planned_expense: Number(form.planned_expense) || 0,
        });
        toast.success('Organizasyon güncellendi');
      } else {
        const body = {
          ...form,
          planned_income: Number(form.planned_income) || 0,
          planned_expense: Number(form.planned_expense) || 0,
          sessions: planMode === 'manual' ? sessions : [],
          recurrence: planMode === 'recurring' ? recurrence : null,
        };
        if (planMode === 'manual' && sessions.length === 0) { toast.error('En az bir oturum ekleyin'); setSaving(false); return; }
        if (planMode === 'recurring' && (!recurrence.start_date || !recurrence.end_date || !recurrence.salon_id)) {
          toast.error('Tekrar için tarih aralığı ve salon seçin'); setSaving(false); return;
        }
        const res = await axios.post(`${API}/organizations`, body);
        toast.success(`${res.data?.sessions?.length || 0} oturum takvime işlendi`);
      }
      setShowForm(false); resetForm();
      await Promise.all([loadOrgs(), loadSummary()]);
      onReservationsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/organizations/${id}`);
      setDetail(res.data);
      setDetailTab('overview');
    } catch (e) {
      toast.error('Detay alınamadı');
    }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    await openDetail(detail.organization.id);
    await Promise.all([loadOrgs(), loadSummary()]);
    onReservationsChanged?.();
  };

  const changeStatus = async (org, status) => {
    const labels = { tamamlandi: 'tamamlamak', iptal: 'iptal etmek', aktif: 'aktifleştirmek', planlandi: 'planlandı yapmak' };
    if (!window.confirm(`"${org.name}" organizasyonunu ${labels[status]} istediğinize emin misiniz?${status === 'iptal' ? '\n\nSalon rezervasyonları serbest bırakılacak, finansal geçmiş korunacak.' : ''}`)) return;
    try {
      const res = await axios.put(`${API}/organizations/${org.id}/status`, { status });
      toast.success(res.data?.message || 'Durum güncellendi');
      if (detail) await refreshDetail(); else { await Promise.all([loadOrgs(), loadSummary()]); onReservationsChanged?.(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Durum değiştirilemedi');
    }
  };

  const removeOrg = async (org) => {
    if (!window.confirm(`"${org.name}" organizasyonu KALICI olarak silinecek. Devam edilsin mi?`)) return;
    try {
      await axios.delete(`${API}/organizations/${org.id}`);
      toast.success('Organizasyon silindi');
      setDetail(null);
      await Promise.all([loadOrgs(), loadSummary()]);
      onReservationsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  const startEdit = (org) => {
    setEditingId(org.id);
    setForm({
      name: org.name || '', org_type: org.org_type || 'Diğer', customer_id: org.customer_id || '',
      client_company: org.client_company || '', contact_name: org.contact_name || '',
      contact_phone: org.contact_phone || '', contact_email: org.contact_email || '',
      responsible_personnel_id: org.responsible_personnel_id || '', description: org.description || '',
      internal_notes: org.internal_notes || '', status: org.status || 'planlandi',
      planned_income: org.planned_income || '', planned_expense: org.planned_expense || '',
    });
    setShowForm(true);
  };

  const addSessionsToOrg = async (payload) => {
    try {
      const res = await axios.post(`${API}/organizations/${detail.organization.id}/sessions`, payload);
      toast.success(res.data?.message || 'Oturum eklendi');
      setShowSessionAdd(false);
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Oturum eklenemedi');
    }
  };

  const saveSession = async () => {
    try {
      await axios.put(`${API}/organization-sessions/${editingSession.id}`, editingSession);
      toast.success('Oturum güncellendi (takvim senkronlandı)');
      setEditingSession(null);
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const deleteSession = async (s) => {
    if (!window.confirm(`${trDate(s.date)} ${s.start_time}–${s.end_time} oturumu silinecek ve salon rezervasyonu serbest bırakılacak. Devam?`)) return;
    try {
      await axios.delete(`${API}/organization-sessions/${s.id}`);
      toast.success('Oturum silindi');
      await refreshDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  const kpis = useMemo(() => ([
    { key: 'total', label: 'Toplam Organizasyon', value: summary?.total ?? 0, raw: true, icon: CalendarRange, tone: 'indigo', color: 'text-indigo-600' },
    { key: 'active', label: 'Aktif', value: summary?.active ?? 0, raw: true, icon: CheckCircle2, tone: 'emerald', color: 'text-emerald-600' },
    { key: 'completed', label: 'Tamamlanan', value: summary?.completed ?? 0, raw: true, icon: CheckCircle2, tone: 'slate', color: 'text-slate-500' },
    { key: 'upcoming', label: 'Yaklaşan', value: summary?.upcoming ?? 0, raw: true, icon: Clock, tone: 'amber', color: 'text-amber-600' },
    { key: 'income_collected', label: 'Tahsil Edilen Gelir', value: summary?.income_collected ?? 0, icon: TrendingUp, tone: 'emerald', color: 'text-emerald-600' },
    { key: 'expense_paid', label: 'Ödenen Gider', value: summary?.expense_paid ?? 0, icon: TrendingDown, tone: 'rose', color: 'text-rose-600' },
    { key: 'cash_result', label: 'Net Nakit Sonucu', value: summary?.cash_result ?? 0, icon: Wallet, tone: 'sky', color: 'text-cyan-600' },
    { key: 'pending_receivable', label: 'Bekleyen Tahsilat', value: summary?.pending_receivable ?? 0, icon: AlertTriangle, tone: 'amber', color: 'text-amber-600' },
    { key: 'pending_payable', label: 'Bekleyen Ödeme', value: summary?.pending_payable ?? 0, icon: AlertTriangle, tone: 'amber', color: 'text-orange-600' },
    { key: 'net_profit', label: 'Toplam Kârlılık', value: summary?.net_profit ?? 0, icon: TrendingUp, tone: 'indigo', color: 'text-indigo-600' },
  ]), [summary]);

  return (
    <div className="space-y-4" data-testid="organizations-tab">
      <PageHeader
        icon={CalendarRange}
        title="Organizasyonlar"
        description="Turnuva, lig ve etkinliklerin planlaması, salon rezervasyonu ve muhasebesi"
        testid="org-page-header"
        actions={
          <>
            <div className="flex gap-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-1">
              <button onClick={() => setMainView('list')} data-testid="org-view-list-btn"
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${mainView === 'list' ? 'bg-[#0B172A] text-white' : 'text-slate-600 hover:bg-white'}`}>
                Organizasyonlar
              </button>
              <button onClick={() => setMainView('reports')} data-testid="org-view-reports-btn"
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors inline-flex items-center gap-1 ${mainView === 'reports' ? 'bg-[#0B172A] text-white' : 'text-slate-600 hover:bg-white'}`}>
                <BarChart3 className="w-3.5 h-3.5" /> Raporlar
              </button>
            </div>
            <Button variant="outline" onClick={() => { loadOrgs(); loadSummary(); }} className="h-9" data-testid="org-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Yenile
            </Button>
            <Button onClick={() => { resetForm(); setShowForm(true); }} className="h-9 bg-[#176B91] hover:bg-[#145a7a]" data-testid="org-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni Organizasyon
            </Button>
          </>
        }
      />

      {mainView === 'reports' ? <OrgReports salons={salons} types={types} /> : (<>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5" data-testid="org-kpis">
        {kpis.map(k => (
          <StatCard
            key={k.key}
            icon={k.icon}
            label={k.label}
            tone={k.tone}
            testid={`org-kpi-${k.key}`}
            value={summary === null ? '...' : (k.raw ? k.value : tl(k.value))}
          />
        ))}
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[11px]">Ara (ad / kod / kurum)</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <Input className="pl-8 h-9" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Avrasya Corporate..." data-testid="org-search-input" />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Durum</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="h-9 w-36" data-testid="org-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Tür</Label>
            <Select value={filters.org_type} onValueChange={(v) => setFilters({ ...filters, org_type: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="org-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Salon</Label>
            <Select value={filters.salon_id} onValueChange={(v) => setFilters({ ...filters, salon_id: v })}>
              <SelectTrigger className="h-9 w-40" data-testid="org-filter-salon"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Kârlılık</Label>
            <Select value={filters.profitability} onValueChange={(v) => setFilters({ ...filters, profitability: v })}>
              <SelectTrigger className="h-9 w-32" data-testid="org-filter-profit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="profit">Kârda</SelectItem>
                <SelectItem value="loss">Zararda</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Başlangıç</Label>
            <Input type="date" className="h-9 w-36" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="org-filter-start" />
          </div>
          <div>
            <Label className="text-[11px]">Bitiş</Label>
            <Input type="date" className="h-9 w-36" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="org-filter-end" />
          </div>
          <div className="flex gap-1">
            <Button variant={view === 'table' ? 'default' : 'outline'} size="sm" className="h-9" onClick={() => setView('table')} data-testid="org-view-table"><List className="w-4 h-4" /></Button>
            <Button variant={view === 'card' ? 'default' : 'outline'} size="sm" className="h-9" onClick={() => setView('card')} data-testid="org-view-card"><LayoutGrid className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      {loading ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm py-14 text-center text-slate-400">Yükleniyor...</div>
      ) : view === 'table' ? (
        <ProTable
          testid="org-table"
          rows={orgs}
          rowKey={(o) => o.id}
          onRowClick={(o) => openDetail(o.id)}
          emptyText="Kayıt bulunamadı. Sağ üstten Yeni Organizasyon ekleyebilirsiniz."
          emptyIcon={CalendarRange}
          initialSort={{ key: 'first_session', dir: 'desc' }}
          columns={[
            {
              key: 'name', label: 'Organizasyon', sortable: true, hideable: false,
              render: (o) => (
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{o.name}</p>
                  <p className="text-[11px] text-slate-500">{o.code} · {o.session_count} oturum</p>
                </div>
              ),
            },
            { key: 'org_type', label: 'Tür', width: 140, sortable: true, render: (o) => <span className="text-[12px] text-slate-700">{o.org_type}</span> },
            {
              key: 'first_session', label: 'Tarih', width: 160, sortable: true,
              render: (o) => <span className="text-[12px] text-slate-600 whitespace-nowrap">{trDate(o.first_session)} – {trDate(o.last_session)}</span>,
            },
            {
              key: 'salon_names', label: 'Salon', width: 170, sortable: true,
              value: (o) => (o.salon_names || []).join(', '),
              render: (o) => <span className="text-[12px] text-slate-600 block truncate max-w-[170px]">{(o.salon_names || []).join(', ') || '-'}</span>,
            },
            { key: 'responsible_name', label: 'Sorumlu', width: 130, sortable: true, render: (o) => <span className="text-[12px] text-slate-600">{o.responsible_name || '-'}</span> },
            {
              key: 'status', label: 'Durum', width: 110, align: 'center', sortable: true,
              render: (o) => <Badge variant="outline" className={`text-[10px] ${STATUS[o.status]?.cls}`}>{STATUS[o.status]?.label}</Badge>,
            },
            {
              key: 'income', label: 'Gelir', width: 118, align: 'right', sortable: true,
              value: (o) => o.financials?.income_total || 0,
              render: (o) => <span className="text-emerald-700 font-medium">{tl(o.financials?.income_total)}</span>,
            },
            {
              key: 'expense', label: 'Gider', width: 118, align: 'right', sortable: true,
              value: (o) => o.financials?.expense_total || 0,
              render: (o) => <span className="text-rose-700 font-medium">{tl(o.financials?.expense_total)}</span>,
            },
            {
              key: 'net', label: 'Net', width: 120, align: 'right', sortable: true, hideable: false,
              value: (o) => o.financials?.net_profit || 0,
              render: (o) => <span className={`font-bold ${(o.financials?.net_profit || 0) < 0 ? 'text-rose-700' : 'text-slate-900'}`}>{tl(o.financials?.net_profit)}</span>,
            },
            {
              key: 'actions', label: 'İşlem', width: 96, align: 'right', hideable: false,
              render: (o) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#176B91]" onClick={() => startEdit(o)} data-testid={`org-edit-${o.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  {isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => removeOrg(o)} data-testid={`org-delete-${o.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div>
              ),
            },
          ]}
        />
      ) : orgs.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm py-14 text-center text-slate-400" data-testid="org-empty">
          Kayıt bulunamadı. Sağ üstten <strong>Yeni Organizasyon</strong> ekleyebilirsiniz.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="org-cards">
          {orgs.map(o => (
            <button key={o.id} className="text-left bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow relative overflow-hidden" onClick={() => openDetail(o.id)} data-testid={`org-card-${o.id}`}>
              <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#176B91]" />
              <div className="p-4 pl-5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{o.name}</p>
                    <p className="text-[11px] text-slate-500">{o.code} · {o.org_type}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS[o.status]?.cls}`}>{STATUS[o.status]?.label}</Badge>
                </div>
                <div className="text-[12px] text-slate-600 space-y-1">
                  <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> {trDate(o.first_session)} – {trDate(o.last_session)} ({o.session_count} oturum)</p>
                  <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {(o.salon_names || []).join(', ') || '-'}</p>
                  <p className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-slate-400" /> {o.responsible_name || 'Sorumlu atanmadı'}</p>
                </div>
                <div className="grid grid-cols-3 gap-1 pt-2 border-t border-[#E2E8F0] text-center">
                  <div><p className="text-[10px] text-slate-500">Gelir</p><p className="text-[12px] font-bold text-emerald-700">{tl(o.financials?.income_total)}</p></div>
                  <div><p className="text-[10px] text-slate-500">Gider</p><p className="text-[12px] font-bold text-rose-700">{tl(o.financials?.expense_total)}</p></div>
                  <div><p className="text-[10px] text-slate-500">Net</p><p className="text-[12px] font-bold text-slate-900">{tl(o.financials?.net_profit)}</p></div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      </>)}

      {/* ===== Yeni / Düzenle Formu ===== */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="org-form-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Organizasyonu Düzenle' : 'Yeni Organizasyon'}</DialogTitle>
            <DialogDescription>
              {editingId ? 'Bilgileri güncelle (oturumlar detay sayfasından yönetilir)' : 'Bilgileri girin, oturumlar otomatik olarak takvime ve rezervasyonlara işlenecek'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Organizasyon Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Avrasya Corporate Voleybol Ligi" data-testid="org-form-name" />
              </div>
              <div>
                <Label className="text-xs">Tür</Label>
                <Select value={form.org_type} onValueChange={(v) => setForm({ ...form, org_type: v })}>
                  <SelectTrigger data-testid="org-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="org-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planlandi">Planlandı</SelectItem>
                    <SelectItem value="aktif">Aktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Düzenleyen Kurum (müşteri)</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="org-form-customer"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Organizasyon Sorumlusu</Label>
                <Select value={form.responsible_personnel_id} onValueChange={(v) => setForm({ ...form, responsible_personnel_id: v })}>
                  <SelectTrigger data-testid="org-form-responsible"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                  <SelectContent>
                    {personnelList.filter(p => p.active !== false).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">İlgili Kişi</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="org-form-contact-name" />
              </div>
              <div>
                <Label className="text-xs">İletişim Telefonu</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="org-form-contact-phone" />
              </div>
              <div>
                <Label className="text-xs">Planlanan Gelir (₺)</Label>
                <Input type="number" value={form.planned_income} onChange={(e) => setForm({ ...form, planned_income: e.target.value })} data-testid="org-form-planned-income" />
              </div>
              <div>
                <Label className="text-xs">Planlanan Gider (₺)</Label>
                <Input type="number" value={form.planned_expense} onChange={(e) => setForm({ ...form, planned_expense: e.target.value })} data-testid="org-form-planned-expense" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Açıklama</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="org-form-description" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">İç Notlar (sadece ekip görür)</Label>
                <Textarea rows={2} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} data-testid="org-form-notes" />
              </div>
            </div>

            {!editingId && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-emerald-900">Tarih ve Saat Planlaması</p>
                  <div className="flex gap-1 ml-auto">
                    <Button type="button" size="sm" variant={planMode === 'manual' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setPlanMode('manual')} data-testid="org-plan-manual">Tek tek ekle</Button>
                    <Button type="button" size="sm" variant={planMode === 'recurring' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setPlanMode('recurring')} data-testid="org-plan-recurring">Otomatik tekrar</Button>
                  </div>
                </div>

                {planMode === 'manual' ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                      <div><Label className="text-[11px]">Tarih</Label><Input type="date" className="h-9" value={sessionDraft.date} onChange={(e) => setSessionDraft({ ...sessionDraft, date: e.target.value })} data-testid="org-session-date" /></div>
                      <div><Label className="text-[11px]">Başlangıç</Label><Input type="time" className="h-9" value={sessionDraft.start_time} onChange={(e) => setSessionDraft({ ...sessionDraft, start_time: e.target.value })} data-testid="org-session-start" /></div>
                      <div><Label className="text-[11px]">Bitiş</Label><Input type="time" className="h-9" value={sessionDraft.end_time} onChange={(e) => setSessionDraft({ ...sessionDraft, end_time: e.target.value })} data-testid="org-session-end" /></div>
                      <div>
                        <Label className="text-[11px]">Salon</Label>
                        <Select value={sessionDraft.salon_id} onValueChange={(v) => setSessionDraft({ ...sessionDraft, salon_id: v })}>
                          <SelectTrigger className="h-9" data-testid="org-session-salon"><SelectValue placeholder="Seç" /></SelectTrigger>
                          <SelectContent>{salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Button type="button" onClick={addSessionToDraft} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="org-session-add"><Plus className="w-4 h-4 mr-1" /> Ekle</Button>
                    </div>
                    {sessions.length > 0 && (
                      <div className="space-y-1" data-testid="org-session-draft-list">
                        {sessions.map((s, i) => (
                          <div key={i} className="flex items-center justify-between bg-white rounded border px-2 py-1 text-xs">
                            <span>{trDate(s.date)} · {s.start_time}–{s.end_time} · {salons.find(x => x.id === s.salon_id)?.name}</span>
                            <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-600" onClick={() => setSessions(sessions.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
                          </div>
                        ))}
                        <p className="text-[11px] text-emerald-800 font-medium">{sessions.length} oturum eklenecek</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-[11px]">Tekrar</Label>
                        <Select value={recurrence.mode} onValueChange={(v) => setRecurrence({ ...recurrence, mode: v })}>
                          <SelectTrigger className="h-9" data-testid="org-rec-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Haftalık (seçilen günler)</SelectItem>
                            <SelectItem value="monthly_dow">Aylık (ayın aynı günü)</SelectItem>
                            <SelectItem value="daily">Her gün</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-[11px]">Başlangıç</Label><Input type="date" className="h-9" value={recurrence.start_date} onChange={(e) => setRecurrence({ ...recurrence, start_date: e.target.value })} data-testid="org-rec-start-date" /></div>
                      <div><Label className="text-[11px]">Bitiş</Label><Input type="date" className="h-9" value={recurrence.end_date} onChange={(e) => setRecurrence({ ...recurrence, end_date: e.target.value })} data-testid="org-rec-end-date" /></div>
                      <div>
                        <Label className="text-[11px]">Salon</Label>
                        <Select value={recurrence.salon_id} onValueChange={(v) => setRecurrence({ ...recurrence, salon_id: v })}>
                          <SelectTrigger className="h-9" data-testid="org-rec-salon"><SelectValue placeholder="Seç" /></SelectTrigger>
                          <SelectContent>{salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-[11px]">Başlama saati</Label><Input type="time" className="h-9" value={recurrence.start_time} onChange={(e) => setRecurrence({ ...recurrence, start_time: e.target.value })} data-testid="org-rec-start-time" /></div>
                      <div><Label className="text-[11px]">Bitiş saati</Label><Input type="time" className="h-9" value={recurrence.end_time} onChange={(e) => setRecurrence({ ...recurrence, end_time: e.target.value })} data-testid="org-rec-end-time" /></div>
                    </div>
                    {recurrence.mode !== 'daily' && (
                      <div>
                        <Label className="text-[11px]">Günler</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {WEEKDAYS.map((d, i) => (
                            <button key={d} type="button"
                              onClick={() => setRecurrence({ ...recurrence, weekdays: recurrence.weekdays.includes(i) ? recurrence.weekdays.filter(x => x !== i) : [...recurrence.weekdays, i] })}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-all ${recurrence.weekdays.includes(i) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}
                              data-testid={`org-rec-day-${i}`}>
                              {d}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-emerald-800">Seçilen aralıkta uygun tüm günler için oturum ve rezervasyon otomatik oluşturulur.</p>
                  </div>
                )}
              </div>
            )}

            <Button onClick={submitOrg} disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="org-form-submit">
              {saving ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Organizasyonu Oluştur ve Takvime İşle')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Detay ===== */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="org-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.organization.name}
                  <Badge variant="outline" className={`text-[10px] ${STATUS[detail.organization.status]?.cls}`}>{STATUS[detail.organization.status]?.label}</Badge>
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {detail.organization.code} · {detail.organization.org_type} · {trDate(detail.organization.first_session)} – {trDate(detail.organization.last_session)}
                  {detail.organization.responsible_name ? ` · Sorumlu: ${detail.organization.responsible_name}` : ''}
                  {(detail.organization.salon_names || []).length ? ` · ${detail.organization.salon_names.join(', ')}` : ''}
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-1 border-b pb-2 flex-wrap">
                {[['overview', 'Genel Bakış'], ['incomes', 'Gelirler'], ['expenses', 'Giderler'], ['report', 'Finansal Rapor'], ['sessions', 'Takvim ve Oturumlar'], ['logs', 'İşlem Geçmişi']].map(([k, l]) => (
                  <button key={k} onClick={() => setDetailTab(k)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${detailTab === k ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    data-testid={`org-detail-tab-${k}`}>
                    {l}
                  </button>
                ))}
              </div>

              {detailTab === 'overview' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      ['Planlanan Gelir', detail.organization.planned_income, 'text-slate-700'],
                      ['Gerçekleşen Gelir', detail.organization.financials?.income_total, 'text-emerald-700'],
                      ['Tahsil Edilen', detail.organization.financials?.income_collected, 'text-emerald-600'],
                      ['Bekleyen Alacak', detail.organization.financials?.pending_receivable, 'text-amber-700'],
                      ['Planlanan Gider', detail.organization.planned_expense, 'text-slate-700'],
                      ['Gerçekleşen Gider', detail.organization.financials?.expense_total, 'text-rose-700'],
                      ['Ödenen Gider', detail.organization.financials?.expense_paid, 'text-rose-600'],
                      ['Bekleyen Ödeme', detail.organization.financials?.pending_payable, 'text-orange-700'],
                    ].map(([label, val, cls]) => (
                      <div key={label} className="rounded-lg border bg-white p-2.5">
                        <p className="text-[10px] text-slate-500 uppercase">{label}</p>
                        <p className={`text-sm font-bold ${cls}`}>{tl(val)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-[10px] text-emerald-700 uppercase">Net Kâr/Zarar</p>
                      <p className={`text-xl font-bold ${(detail.organization.financials?.net_profit || 0) < 0 ? 'text-rose-700' : 'text-emerald-800'}`} data-testid="org-detail-net-profit">
                        {tl(detail.organization.financials?.net_profit)}
                      </p>
                    </div>
                    <div className="rounded-lg border-2 border-cyan-200 bg-cyan-50 p-3">
                      <p className="text-[10px] text-cyan-700 uppercase">Nakit Bazlı Sonuç</p>
                      <p className="text-xl font-bold text-cyan-800">{tl(detail.organization.financials?.cash_result)}</p>
                    </div>
                    <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                      <p className="text-[10px] text-indigo-700 uppercase">Kâr Marjı</p>
                      <p className="text-xl font-bold text-indigo-800">{(detail.organization.financials?.profit_margin || 0)}%</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2">
                    Bekleyen gelir/gider kasayı etkilemez; sadece tahsil edilen/ödenen tutar seçilen kasaya tek hareket olarak işlenir.
                    Oturumlar takvime 0 ₺ rezervasyon olarak yazılır (salon dolu görünür, kasa etkilenmez).
                  </p>
                  {detail.organization.description && <p className="text-sm text-slate-700"><strong>Açıklama:</strong> {detail.organization.description}</p>}
                  <div className="flex flex-wrap gap-2 pt-1 border-t">
                    <Button size="sm" variant="outline" onClick={() => { setDetail(null); startEdit(detail.organization); }} data-testid="org-detail-edit"><Pencil className="w-3.5 h-3.5 mr-1" /> Düzenle</Button>
                    <Button size="sm" variant="outline" onClick={() => setDetailTab('incomes')} data-testid="org-detail-quick-income"><Plus className="w-3.5 h-3.5 mr-1" /> Gelir Ekle</Button>
                    <Button size="sm" variant="outline" onClick={() => setDetailTab('expenses')} data-testid="org-detail-quick-expense"><Plus className="w-3.5 h-3.5 mr-1" /> Gider Ekle</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowSessionAdd(true)} data-testid="org-detail-add-session"><Plus className="w-3.5 h-3.5 mr-1" /> Oturum Ekle</Button>
                    {detail.organization.status !== 'tamamlandi' && (
                      <Button size="sm" className="bg-slate-700 hover:bg-slate-800" onClick={() => changeStatus(detail.organization, 'tamamlandi')} data-testid="org-detail-complete"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Tamamla</Button>
                    )}
                    {detail.organization.status === 'tamamlandi' && (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(detail.organization, 'aktif')} data-testid="org-detail-reactivate">Yeniden Aktifleştir</Button>
                    )}
                    {detail.organization.status !== 'iptal' ? (
                      <Button size="sm" variant="outline" className="text-rose-700 border-rose-200" onClick={() => changeStatus(detail.organization, 'iptal')} data-testid="org-detail-cancel"><X className="w-3.5 h-3.5 mr-1" /> İptal Et</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(detail.organization, 'aktif')} data-testid="org-detail-restore">İptali Kaldır</Button>
                    )}
                  </div>
                </div>
              )}

              {detailTab === 'incomes' && (
                <OrgFinance orgId={detail.organization.id} type="income" sessions={detail.sessions} onChanged={refreshDetail} />
              )}
              {detailTab === 'expenses' && (
                <OrgFinance orgId={detail.organization.id} type="expense" sessions={detail.sessions} onChanged={refreshDetail} />
              )}
              {detailTab === 'report' && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <span className="text-[11px] text-slate-500 mr-auto">Rapor: özet + oturumlar + gelir/gider dökümü + bekleyen alacak/ödemeler</span>
                    <Button size="sm" className="h-8 bg-slate-800 hover:bg-slate-900"
                      onClick={() => downloadReport(`${API}/organizations/${detail.organization.id}/report/export`, { format: 'pdf' }, `organizasyon_${detail.organization.code}.pdf`)}
                      data-testid="org-detail-pdf-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> PDF İndir</Button>
                    <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800"
                      onClick={() => downloadReport(`${API}/organizations/${detail.organization.id}/report/export`, { format: 'excel' }, `organizasyon_${detail.organization.code}.xlsx`)}
                      data-testid="org-detail-excel-btn"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel İndir</Button>
                  </div>
                  <OrgFinancialReport orgId={detail.organization.id} />
                </div>
              )}

              {detailTab === 'sessions' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">{detail.sessions.length} oturum</p>
                    <Button size="sm" onClick={() => setShowSessionAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid="org-sessions-add-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Oturum Ekle</Button>
                  </div>
                  <div className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="org-sessions-list">
                    {detail.sessions.length === 0 ? (
                      <p className="text-center py-8 text-slate-400 text-sm">Oturum yok</p>
                    ) : detail.sessions.map(s => (
                      <div key={s.id} className="rounded-lg border p-2.5 bg-white" data-testid={`org-session-${s.id}`}>
                        {editingSession?.id === s.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              <Input type="date" className="h-8" value={editingSession.date} onChange={(e) => setEditingSession({ ...editingSession, date: e.target.value })} data-testid="org-session-edit-date" />
                              <Input type="time" className="h-8" value={editingSession.start_time} onChange={(e) => setEditingSession({ ...editingSession, start_time: e.target.value })} data-testid="org-session-edit-start" />
                              <Input type="time" className="h-8" value={editingSession.end_time} onChange={(e) => setEditingSession({ ...editingSession, end_time: e.target.value })} data-testid="org-session-edit-end" />
                              <Select value={editingSession.salon_id} onValueChange={(v) => setEditingSession({ ...editingSession, salon_id: v })}>
                                <SelectTrigger className="h-8" data-testid="org-session-edit-salon"><SelectValue /></SelectTrigger>
                                <SelectContent>{salons.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={saveSession} data-testid="org-session-save">Kaydet</Button>
                              <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingSession(null)}>İptal</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm">
                              <p className="font-medium text-slate-800">{trDate(s.date)} · {s.start_time}–{s.end_time}</p>
                              <p className="text-[11px] text-slate-500">{s.salon_name}{s.title ? ` · ${s.title}` : ''}{s.reservation_id ? ' · takvimde' : ' · rezervasyon yok'}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600" onClick={() => setEditingSession({ ...s })} data-testid={`org-session-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => deleteSession(s)} data-testid={`org-session-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailTab === 'logs' && (
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto" data-testid="org-logs-list">
                  {detail.logs.length === 0 ? (
                    <p className="text-center py-8 text-slate-400 text-sm">Kayıt yok</p>
                  ) : detail.logs.map(l => (
                    <div key={l.id} className="rounded border p-2 text-xs bg-white">
                      <p className="font-medium text-slate-800">{l.description}</p>
                      <p className="text-[11px] text-slate-500">{l.user} · {new Date(l.created_at).toLocaleString('tr-TR')}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Oturum ekleme modalı (detaydan) */}
      <Dialog open={showSessionAdd} onOpenChange={setShowSessionAdd}>
        <DialogContent className="max-w-lg" data-testid="org-session-add-modal">
          <DialogHeader>
            <DialogTitle>Oturum Ekle</DialogTitle>
            <DialogDescription>Tek oturum veya tarih aralığında otomatik tekrar</DialogDescription>
          </DialogHeader>
          <SessionAddForm salons={salons} onSubmit={addSessionsToOrg} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SessionAddForm = ({ salons, onSubmit }) => {
  const [mode, setMode] = useState('single');
  const [single, setSingle] = useState({ date: '', start_time: '15:00', end_time: '22:00', salon_id: '', title: '' });
  const [rec, setRec] = useState({ mode: 'weekly', start_date: '', end_date: '', weekdays: [], start_time: '15:00', end_time: '22:00', salon_id: '' });

  const submit = () => {
    if (mode === 'single') {
      if (!single.date || !single.salon_id) { toast.error('Tarih ve salon seçin'); return; }
      onSubmit({ sessions: [single] });
    } else {
      if (!rec.start_date || !rec.end_date || !rec.salon_id) { toast.error('Tarih aralığı ve salon seçin'); return; }
      onSubmit({ recurrence: rec });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        <Button size="sm" variant={mode === 'single' ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setMode('single')} data-testid="org-add-mode-single">Tek Oturum</Button>
        <Button size="sm" variant={mode === 'recurring' ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setMode('recurring')} data-testid="org-add-mode-recurring">Otomatik Tekrar</Button>
      </div>
      {mode === 'single' ? (
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[11px]">Tarih</Label><Input type="date" className="h-9" value={single.date} onChange={(e) => setSingle({ ...single, date: e.target.value })} data-testid="org-add-date" /></div>
          <div>
            <Label className="text-[11px]">Salon</Label>
            <Select value={single.salon_id} onValueChange={(v) => setSingle({ ...single, salon_id: v })}>
              <SelectTrigger className="h-9" data-testid="org-add-salon"><SelectValue placeholder="Seç" /></SelectTrigger>
              <SelectContent>{salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[11px]">Başlangıç</Label><Input type="time" className="h-9" value={single.start_time} onChange={(e) => setSingle({ ...single, start_time: e.target.value })} data-testid="org-add-start" /></div>
          <div><Label className="text-[11px]">Bitiş</Label><Input type="time" className="h-9" value={single.end_time} onChange={(e) => setSingle({ ...single, end_time: e.target.value })} data-testid="org-add-end" /></div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-[11px]">Başlangıç</Label><Input type="date" className="h-9" value={rec.start_date} onChange={(e) => setRec({ ...rec, start_date: e.target.value })} data-testid="org-add-rec-start" /></div>
            <div><Label className="text-[11px]">Bitiş</Label><Input type="date" className="h-9" value={rec.end_date} onChange={(e) => setRec({ ...rec, end_date: e.target.value })} data-testid="org-add-rec-end" /></div>
            <div><Label className="text-[11px]">Saat (başlangıç)</Label><Input type="time" className="h-9" value={rec.start_time} onChange={(e) => setRec({ ...rec, start_time: e.target.value })} /></div>
            <div><Label className="text-[11px]">Saat (bitiş)</Label><Input type="time" className="h-9" value={rec.end_time} onChange={(e) => setRec({ ...rec, end_time: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="text-[11px]">Salon</Label>
              <Select value={rec.salon_id} onValueChange={(v) => setRec({ ...rec, salon_id: v })}>
                <SelectTrigger className="h-9" data-testid="org-add-rec-salon"><SelectValue placeholder="Seç" /></SelectTrigger>
                <SelectContent>{salons.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Günler</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {WEEKDAYS.map((d, i) => (
                <button key={d} type="button"
                  onClick={() => setRec({ ...rec, weekdays: rec.weekdays.includes(i) ? rec.weekdays.filter(x => x !== i) : [...rec.weekdays, i] })}
                  className={`px-2.5 py-1 rounded-full text-xs border ${rec.weekdays.includes(i) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid={`org-add-rec-day-${i}`}>{d}</button>
              ))}
            </div>
          </div>
        </div>
      )}
      <Button onClick={submit} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="org-add-submit">Takvime İşle</Button>
    </div>
  );
};

export default OrganizationsTab;
